import * as React from 'react'
import { format } from 'date-fns'
import { Search, Users, Calendar, TrendingUp, FileText, Download, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import { customerApi } from '@/api/customers'
import { getOrders, generateDispatchDocument } from '@/api/orders'
import type { Customer } from '@/types'
import type { Order } from '@/api/orders'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

type SortKey = 'date' | 'status' | 'amount'
type SortDir = 'asc' | 'desc'

export default function CustomerReportPage() {
  const [customers, setCustomers] = React.useState<Customer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = React.useState('')
  const [dateFrom, setDateFrom] = React.useState(() => {
    const d = new Date()
    d.setDate(1)
    return format(d, 'yyyy-MM-dd')
  })
  const [dateTo, setDateTo] = React.useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [orders, setOrders] = React.useState<Order[]>([])
  const [loading, setLoading] = React.useState(false)
  const [searched, setSearched] = React.useState(false)
  const [attachDispatch, setAttachDispatch] = React.useState(false)
  const [exportingPdf, setExportingPdf] = React.useState(false)
  const [sortKey, setSortKey] = React.useState<SortKey>('date')
  const [sortDir, setSortDir] = React.useState<SortDir>('asc')

  React.useEffect(() => {
    customerApi.getCustomers(1, 100).then(res => setCustomers(res.items || []))
  }, [])

  const handleSearch = async () => {
    if (!selectedCustomerId) return
    setLoading(true)
    try {
      const res = await getOrders({
        limit: 1000,
        customer_id: selectedCustomerId,
        date_from: dateFrom,
        date_to: dateTo,
      })
      setOrders(res.data)
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const STATUS_ORDER: Record<string, number> = { '待派車': 0, '已派車': 1, '進行中': 2, '已完成': 3, '已取消': 4 }

  const sortedOrders = React.useMemo(() => {
    return [...orders].sort((a, b) => {
      let cmp = 0
      if (sortKey === 'date') {
        cmp = (a.pickup_datetime || '').localeCompare(b.pickup_datetime || '')
      } else if (sortKey === 'status') {
        cmp = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9)
      } else if (sortKey === 'amount') {
        cmp = (a.pricing?.total_amount || 0) - (b.pricing?.total_amount || 0)
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [orders, sortKey, sortDir])

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId)
  const totalAmount = Math.round(orders.reduce((sum, o) => sum + (o.pricing?.total_amount || 0), 0))
  const totalTaxAmount = Math.round(totalAmount * 1.05)

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 opacity-40" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-green-600" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-green-600" />
  }

  const statusColor: Record<string, string> = {
    '待派車': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    '已派車': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    '進行中': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    '已完成': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    '已取消': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }

  const tableRows = sortedOrders.map((order, idx) => [
    idx + 1,
    order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-',
    order.passenger_name || '',
    order.passenger_phone || '',
    order.pickup_location || '',
    order.dropoff_location || '',
    order.payment_method || '',
    Math.round(order.pricing?.total_amount || 0),
  ])

  const handleExportExcel = async () => {
    const { utils, writeFile } = await import('xlsx')
    const headers = ['#', '日期時間', '乘客姓名', '乘客電話', '上車地點', '下車地點', '付款方式', '總金額']
    const wsData = [
      [`客戶報表 - ${selectedCustomer?.name}`, '', '', '', '', '', '', '', ''],
      [`期間：${dateFrom} ～ ${dateTo}`, '', '', '', '', '', '', '', ''],
      [],
      headers,
      ...tableRows,
      [],
      ['', '', '', '', '', '', '', '小計', totalAmount],
      ['', '', '', '', '', '', '', '含稅(5%)', totalTaxAmount],
    ]
    const ws = utils.aoa_to_sheet(wsData)
    ws['!cols'] = [{ wch: 4 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 30 }, { wch: 30 }, { wch: 8 }, { wch: 10 }, { wch: 10 }]
    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, '客戶報表')
    writeFile(wb, `客戶報表_${selectedCustomer?.name}_${dateFrom}_${dateTo}.xlsx`)
  }

  const handleExportCSV = () => {
    const headers = ['#', '日期時間', '乘客姓名', '乘客電話', '上車地點', '下車地點', '付款方式', '總金額']
    const escapeCsv = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`
    const lines = [
      headers.map(escapeCsv).join(','),
      ...tableRows.map(row => row.map(escapeCsv).join(',')),
    ]
    const csvContent = '﻿' + lines.join('\r\n') // BOM 避免 Excel 開啟中文亂碼
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `客戶報表_${selectedCustomer?.name}_${dateFrom}_${dateTo}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // 將圖片 Blob 轉成 data URL，供 <img> 直接嵌入列印內容
  const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })

  const handleExportPDF = async () => {
    setExportingPdf(true)
    let attachmentsHtml = ''
    try {
      if (attachDispatch) {
        const images: string[] = []
        for (const order of orders) {
          try {
            const blob = await generateDispatchDocument(order.id, true)
            images.push(await blobToDataUrl(blob))
          } catch (err) {
            console.error(`派車單生成失敗 (${order.order_number}):`, err)
          }
        }
        attachmentsHtml = images.map(src => `
          <div class="attachment-page">
            <img src="${src}" />
          </div>
        `).join('')
      }

      const rows = orders.map((order, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-'}</td>
          <td>${order.passenger_name || ''}</td>
          <td>${order.passenger_phone || ''}</td>
          <td>${order.pickup_location || ''}</td>
          <td>${order.dropoff_location || ''}</td>
          <td>${order.payment_method || ''}</td>
          <td style="text-align:right">$${Math.round(order.pricing?.total_amount || 0).toLocaleString()}</td>
        </tr>
      `).join('')

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>客戶報表</title>
  <style>
    @page { size: A4 portrait; margin: 15mm; }
    body { font-family: "Microsoft JhengHei", "PingFang TC", sans-serif; font-size: 11px; color: #111; }
    h2 { font-size: 16px; margin: 0 0 4px; }
    p { margin: 0 0 12px; color: #555; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #16a34a; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; }
    td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
    tr:nth-child(even) td { background: #f9fafb; }
    .total-row td { font-weight: bold; background: #f0fdf4; border-top: 2px solid #16a34a; }
    .attachment-page { page-break-before: always; display: flex; align-items: center; justify-content: center; height: 100%; }
    .attachment-page img { max-width: 100%; max-height: 100%; object-fit: contain; }
  </style>
</head>
<body>
  <h2>客戶報表 - ${selectedCustomer?.name}</h2>
  <p>期間：${dateFrom} ～ ${dateTo}　共 ${orders.length} 筆</p>
  <table>
    <thead>
      <tr>
        <th>#</th><th>日期時間</th><th>乘客姓名</th><th>乘客電話</th>
        <th>上車地點</th><th>下車地點</th><th>付款方式</th><th>總金額</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr class="total-row">
        <td colspan="6"></td>
        <td>小計</td><td style="text-align:right">$${totalAmount.toLocaleString()}</td>
      </tr>
      <tr class="total-row">
        <td colspan="6"></td>
        <td>含稅(5%)</td><td style="text-align:right">$${totalTaxAmount.toLocaleString()}</td>
      </tr>
    </tfoot>
  </table>
  ${attachmentsHtml}
</body>
</html>`

      const win = window.open('', '_blank')
      if (!win) return
      win.document.write(html)
      win.document.close()
      win.onload = () => { win.print() }
    } finally {
      setExportingPdf(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100 dark:bg-green-900/30">
          <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">客戶報表</h1>
      </div>

      {/* 篩選條件 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px] space-y-1">
            <label className="text-xs font-medium text-gray-500">客戶</label>
            <select
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none cursor-pointer"
            >
              <option value="">選擇客戶</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.phone ? ` - ${c.phone}` : ''}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />快速選擇</label>
            <button
              type="button"
              onClick={() => {
                const now = new Date()
                const first = new Date(now.getFullYear(), now.getMonth(), 1)
                const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                const fmt = (d: Date) => d.toISOString().substring(0, 10)
                setDateFrom(fmt(first))
                setDateTo(fmt(last))
              }}
              className="h-10 px-4 rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 text-sm font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors cursor-pointer"
            >
              本月
            </button>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />開始日期</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />結束日期</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={!selectedCustomerId || loading}
            className="h-10 px-6 bg-green-600 hover:bg-green-700 text-white font-bold"
          >
            <Search className="h-4 w-4 mr-2" />
            {loading ? '查詢中...' : '查詢'}
          </Button>
        </div>
      </div>

      {/* 統計卡片 */}
      {searched && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><FileText className="h-4 w-4" />訂單數</div>
            <div className="text-2xl font-black text-gray-900 dark:text-white">{orders.length} 筆</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><TrendingUp className="h-4 w-4" />總金額合計</div>
            <div className="text-2xl font-black text-green-600 dark:text-green-400">${totalAmount.toLocaleString()}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><TrendingUp className="h-4 w-4" />含稅總額合計</div>
            <div className="text-2xl font-black text-orange-500">${totalTaxAmount.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* 訂單列表 */}
      {searched && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="font-bold text-gray-900 dark:text-white text-sm">
              {selectedCustomer?.name} 的訂單明細
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 w-full sm:w-auto">{dateFrom} ～ {dateTo}</span>
              {orders.length > 0 && (
                <>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <Checkbox
                      checked={attachDispatch}
                      onCheckedChange={(v) => setAttachDispatch(!!v)}
                      className="h-4 w-4"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">PDF附上派車單</span>
                  </label>
                  <Button
                    onClick={handleExportCSV}
                    variant="outline"
                    className="h-8 px-3 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    CSV
                  </Button>
                  <Button
                    onClick={handleExportExcel}
                    variant="outline"
                    className="h-8 px-3 text-xs border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Excel
                  </Button>
                  <Button
                    onClick={handleExportPDF}
                    variant="outline"
                    disabled={exportingPdf}
                    className="h-8 px-3 text-xs border-red-300 text-red-600 hover:bg-red-50"
                  >
                    {exportingPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                    {exportingPdf ? '產生中...' : 'PDF'}
                  </Button>
                </>
              )}
            </div>
          </div>
          {orders.length === 0 ? (
            <div className="py-16 text-center text-gray-400">此期間無訂單資料</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-green-600 select-none"
                      onClick={() => handleSort('date')}
                    >日期時間 <SortIcon k="date" /></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">乘客</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">行程</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-green-600 select-none"
                      onClick={() => handleSort('status')}
                    >狀態 <SortIcon k="status" /></th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">付款方式</th>
                    <th
                      className="px-4 py-3 text-right text-xs font-medium text-green-600 uppercase font-black cursor-pointer hover:text-green-700 select-none"
                      onClick={() => handleSort('amount')}
                    >總金額 <SortIcon k="amount" /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sortedOrders.map((order, idx) => (
                    <tr key={order.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-slate-50 dark:bg-gray-750'}>
                      <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {order.pickup_datetime ? format(new Date(order.pickup_datetime), 'MM/dd HH:mm') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{order.passenger_name}</div>
                        <div className="text-xs text-gray-400">{order.passenger_phone}</div>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <div className="text-xs text-gray-500 truncate">{order.pickup_location}</div>
                        <div className="text-xs text-gray-500 truncate">→ {order.dropoff_location}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusColor[order.status] || 'bg-gray-100 text-gray-600'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-500">{order.payment_method}</td>
                      <td className="px-4 py-3 text-right font-black text-green-600 dark:text-green-400">
                        ${Math.round(order.pricing?.total_amount || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-green-50 dark:bg-green-900/20 border-t-2 border-green-200 dark:border-green-700">
                  <tr>
                    <td colSpan={5} className="px-4 py-3 font-bold text-gray-700 dark:text-gray-300">合計</td>
                    <td className="px-4 py-3 text-right text-xs text-gray-500">含稅: ${totalTaxAmount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-black text-green-600 dark:text-green-400 text-base">
                      ${totalAmount.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
